// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  ListItemButton,
  Menu,
  MenuItem,
  MenuProps,
  Pagination,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import CustomBreadcrumbs from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import { PAGE_SIZE } from "../../services/common/const";
import { GetForm3cForm25List } from "../../services/Form3cForm25Service";

import {
  endOfMonth,
  endOfYear,
  format,
  isValid,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";

import { PreferencesContext } from "../../PreferenceContext";
import { useNavigate, useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { GetForm3cForm25Report } from "../../services/ReportService";
import { ExportFilesFunction } from "../../shared/CommonFunction";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { bindTrigger } from "material-ui-popup-state";
import { bindMenu, usePopupState } from "material-ui-popup-state/hooks";
import CheckIcon from "@mui/icons-material/Check";
import { DateRange } from "react-date-range";

// SCHEMA
interface Data {
  billingId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  //onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

// SCHEMA FOR RESULTDATA
interface ResultData {
  form3cForm25Coll: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
  totalReceivedAmount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  form3cForm25Coll: [],
  tabTypeColl: [],
  totalCount: 0,
  totalReceivedAmount: 0,
};

// SCHEMA FOR PARAMETER
interface Parameter {
  billingId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  startDate: Date;
  endDate: Date;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  billingId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  startDate: new Date(),
  endDate: new Date(),
};

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "left",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "left",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "10px !important",
    marginTop: theme.spacing(1),
    minWidth: 280,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px !important",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
  },
}));

const Form3CForm25Report = () => {
  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");

  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [page_Index, setPage_Index] = useState(1);
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [isCustomDate, setIsCustomDate] = React.useState(false);

  const navigate = useNavigate();

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let page = urlParams.get("page");
  let encodedQuery = urlParams.get("q");
  let fd = urlParams.get("fd");
  let ed = urlParams.get("ed");

  let query = "";
  let curPage = 1;

  let startDate: any = null;
  let endDate: any = null;
  let dateRangeHeading: string = "Today";
  let todayDate = new Date();

  if (page != null && page !== "" && page !== undefined) {
    curPage = +page;
  }

  if (encodedQuery !== null && encodedQuery !== undefined) {
    query = encodedQuery;
  }

  const [value, setValue] = useState(query || "");
  const popupState = usePopupState({ variant: "popover", popupId: "demoMenu" });
  const [param, setParam] = useState({
    ...parameters,
    startDate: startDate,
    endDate: endDate,
  });
  const [selectedDateRange, setDateRange] = useState<any>([
    {
      startDate: startDate ? new Date(startDate) : new Date(),
      endDate: endDate ? new Date(endDate) : new Date(),
      key: "selection",
    },
  ]);

  const handleDateRange = (selected: any, popupState: any) => {
    let lStartDate = format(new Date(selected[0].startDate), "yyyy-MM-dd");
    let lEndDate = format(new Date(selected[0].endDate), "yyyy-MM-dd");

    setDateRange([
      {
        startDate: new Date(selected[0].startDate),
        endDate: new Date(selected[0].endDate),
        key: "selection",
      },
    ]);

    popupState.close();
    setIsCustomDate(false);

    navigate(`?page=1&q=${encodedQuery || ""}&fd=${lStartDate}&ed=${lEndDate}`);
  };

  // Update the searchValue function to maintain current date range
  const searchValue = (val: any) => {
    val = val.trim();
    // encode value
    val = encodeURIComponent(val);
    // Maintain current date range in the URL
    navigate(`?page=1&q=${val}&fd=${fd || ""}&ed=${ed || ""}`);
  };

  // Update the clear search functionality
  const handleClearSearch = () => {
    setValue(""); // Clear the input value
    searchValue(""); // Clear the search parameter
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (curPage !== value) {
      navigate(
        `?page=${value}&q=${encodedQuery || ""}&fd=${fd || ""}&ed=${ed || ""}`,
      );
    }
  };

  const daterangeArray = useMemo(
    () => [
      {
        dateHeading: "Today",
        startDate: format(todayDate, "yyyy-MM-dd"),
        endDate: format(todayDate, "yyyy-MM-dd"),
      },
      {
        dateHeading: "Last 7 days",
        startDate: format(subDays(todayDate, 6), "yyyy-MM-dd"),
        endDate: format(todayDate, "yyyy-MM-dd"),
      },
      {
        dateHeading: "Last 15 days",
        startDate: format(subDays(todayDate, 14), "yyyy-MM-dd"),
        endDate: format(todayDate, "yyyy-MM-dd"),
      },
      {
        dateHeading: "Last 30 days",
        startDate: format(subDays(todayDate, 29), "yyyy-MM-dd"),
        endDate: format(todayDate, "yyyy-MM-dd"),
      },
      {
        dateHeading: "This month",
        startDate: format(startOfMonth(todayDate), "yyyy-MM-dd"),
        endDate: format(todayDate, "yyyy-MM-dd"),
      },
      {
        dateHeading: "Last month",
        startDate: format(startOfMonth(subMonths(todayDate, 1)), "yyyy-MM-dd"),
        endDate: format(endOfMonth(subMonths(todayDate, 1)), "yyyy-MM-dd"),
      },
      {
        dateHeading: "This year",
        startDate: format(startOfYear(todayDate), "yyyy-MM-dd"),
        endDate: format(todayDate, "yyyy-MM-dd"),
      },
      {
        dateHeading: "Last year",
        startDate: format(startOfYear(subYears(todayDate, 1)), "yyyy-MM-dd"),
        endDate: format(endOfYear(subYears(todayDate, 1)), "yyyy-MM-dd"),
      },
    ],
    [todayDate],
  );

  if (fd !== null && fd !== undefined && ed !== null && ed !== undefined) {
    if (fd !== "" && ed !== "") {
      if (isValid(new Date(fd)) && isValid(new Date(ed))) {
        startDate = new Date(fd);
        endDate = new Date(ed);
        let dateRange = daterangeArray?.find(
          (x) =>
            x.startDate === format(new Date(fd), "yyyy-MM-dd") &&
            x.endDate === format(new Date(ed), "yyyy-MM-dd"),
        );
        if (dateRange) {
          dateRangeHeading = dateRange?.dateHeading;
        } else {
          dateRangeHeading = "Custom date range";
        }
      }
    } else {
      startDate = null;
      endDate = null;
    }
  } else {
    let defaultRange = daterangeArray.find((x) => x.dateHeading === "Today");
    if (defaultRange && defaultRange.startDate && defaultRange.endDate) {
      startDate = new Date(defaultRange.startDate);
      endDate = new Date(defaultRange.endDate);
    }
  }

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
            align="center"
          >
            Date
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="left"
          >
            Sl. No.
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="left"
          >
            Patient's name
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="left"
          >
            Nature of professional services rendered, i.e., <br />
            general consultation, surgery, injection, visit, etc.
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="right"
          >
            Fees received
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
            align="center"
          >
            Date of receipt
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const getForm3CForm25Report = async () => {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("userId", userContext?.loginDetail.userId?.toString());
      // if (param?.startDate) {
      //   params.append("startDate", format(param.startDate, "yyyy-MM-dd"));
      // }
      // if (param?.endDate) {
      //   params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
      // }
      if (startDate)
        params.append("startDate", format(new Date(startDate), "yyyy-MM-dd"));
      if (endDate)
        params.append("endDate", format(new Date(endDate), "yyyy-MM-dd"));
      params.append("pageIndex", curPage?.toString());
      params.append("pageSize", param.pageSize?.toString());
      // params.append("sortColumn", param.sortColumn);
      // params.append("sortDirection", param.sortDirection);
      params.append("sortColumn", param.sortColumn || "name");
      params.append("sortDirection", param.sortDirection || "ASC");
      params.append("searchValue", query || "");
      params.append("mode", "LIST");

      const result: any = await GetForm3cForm25List(params);

      if (result?.data?.data?.form3cForm25Coll) {
        const resultData: ResultData = result?.data?.data;
        let form3cForm25Coll = resultData?.form3cForm25Coll || [];

        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (curPage - 1) * param.pageSize + 1;
        let endIndex =
          (curPage - 1) * param.pageSize + (form3cForm25Coll?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  async function openForm3CForm25ReportPopup(type: any) {
    var attachmentName = "";
    if (type === "pdf") {
      // attachmentName = "Form3C/Form25.pdf";
      attachmentName =
        "Form3C/Form25" +
        format(new Date(fd), "_yyyyMMdd") +
        format(new Date(ed), "-yyyyMMdd") +
        ".pdf";
    } else {
      attachmentName =
        "Form3C/Form25" +
        format(new Date(fd), "_yyyyMMdd") +
        format(new Date(ed), "-yyyyMMdd") +
        ".xls";
    }
    setIsLoading(true);
    let params = new URLSearchParams();
    params.append("reportName", "Form3CForm25Report");
    params.append("reportType", type);
    params.append("consumerId", userContext.consumerId.toString());
    params.append("organizationId", userContext.organizationId.toString());
    params.append("branchId", userContext.branchId.toString());
    params.append("userId", userContext?.loginDetail.userId?.toString());
    if (param?.startDate) {
      params.append("startDate", format(param.startDate, "yyyy-MM-dd"));
    }
    if (param?.endDate) {
      params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
    }
    params.append("pageIndex", param.pageIndex?.toString());
    params.append("pageSize", param.pageSize?.toString());
    params.append("sortColumn", param.sortColumn);
    params.append("sortDirection", param.sortDirection);
    params.append("searchValue", query || "");
    params.append("fileName", attachmentName);
    params.append("mode", "EXPORT");

    try {
      const result: any = await GetForm3cForm25Report(params);
      if (result && result.data && result.data.data) {
        var fileContent = result?.data?.data?.fileData;
        var attachmentData = fileContent;
        ExportFilesFunction(attachmentName, type, attachmentData);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  }

  // Update the useEffect for param changes to properly handle both search and date
  useEffect(() => {
    setPage_Index(curPage);
    setParam({
      ...parameters,
      searchValue: query,
      pageIndex: curPage,
      startDate: startDate,
      endDate: endDate,
    });
  }, [query, curPage, fd, ed]);

  // FETCH DATA
  const fetchData = async () => {
    try {
      setIsLoading(true);
      await getForm3CForm25Report();
    } catch (e) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [param, curPage, query, fd, ed]);

  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", height: "100%", alignItems: "center" }}
              >
                <CustomBreadcrumbs />
              </Box>
            </Grid>
          </Grid>
          <Grid container className="list-container">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={4} lg={2}>
                <SkeletonWrapper
                  isLoading={isLoading}
                  className="skeleton-search-box"
                >
                  <TextField
                    id="searchBox"
                    placeholder="Search..."
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        searchValue(value);
                      }
                    }}
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(e) => setValue(e.target.value)}
                    value={value}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            size="small"
                            onClick={() => {
                              searchValue(value);
                            }}
                          >
                            {" "}
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: value && (
                        <IconButton
                          aria-label="clear search"
                          onClick={handleClearSearch}
                        >
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </SkeletonWrapper>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={8}>
                <SkeletonWrapper
                  isLoading={isLoading}
                  className="skeleton-search-box"
                >
                  <Button
                    variant="outlined"
                    sx={{
                      color: "text.primary",
                      borderColor: "#c4c4c4",
                      justifyContent: "space-between",
                      width: "100%",
                      maxWidth: "245px",
                      textTransform: "none",
                      height: "40px",
                    }}
                    endIcon={
                      <CalendarTodayIcon sx={{ color: "action.active" }} />
                    }
                    {...bindTrigger(popupState)}
                  >
                    {/* {`${format(new Date(startDate), "MMM d, yyyy")} - ${format(new Date(endDate), "MMM d, yyyy")}`} */}
                    {dateRangeHeading}
                  </Button>
                  <StyledMenu
                    id="demo-customized-menu"
                    MenuListProps={{
                      "aria-labelledby": "demo-customized-button",
                    }}
                    {...bindMenu(popupState)}
                  >
                    {!isCustomDate ? (
                      [
                        ...daterangeArray?.map((range, index) => (
                          <MenuItem
                            key={`date-range-${index}`}
                            sx={{ padding: "0px !important" }}
                            onClick={popupState.close}
                          >
                            <ListItemButton
                              selected={range?.dateHeading === dateRangeHeading}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                py: 1,
                                px: 2,
                                "&.Mui-selected": {
                                  backgroundColor: "transparent",
                                },
                                "&.Mui-selected:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                              }}
                              onClick={(event: any) => {
                                navigate(
                                  `?page=1&q=${encodedQuery || ""}&fd=${range?.startDate || ""}&ed=${range?.endDate || ""}`,
                                );
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                {range?.dateHeading === dateRangeHeading && (
                                  <CheckIcon sx={{ mr: 1, fontSize: 18 }} />
                                )}
                                <Typography
                                  sx={{
                                    ml:
                                      range?.dateHeading === dateRangeHeading
                                        ? 0
                                        : "26px",
                                  }}
                                >
                                  {range?.dateHeading}
                                </Typography>
                              </Box>
                              {range.startDate && range.endDate && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    ml: "26px",
                                    mt: 0.5,
                                  }}
                                >
                                  {range.dateHeading === "Today"
                                    ? format(
                                        new Date(range.startDate),
                                        "MMM d, yyyy",
                                      )
                                    : `${format(
                                        new Date(range.startDate),
                                        "MMM d, yyyy",
                                      )} - ${format(
                                        new Date(range.endDate),
                                        "MMM d, yyyy",
                                      )}`}
                                </Typography>
                              )}
                            </ListItemButton>
                          </MenuItem>
                        )),
                        <MenuItem
                          key="custom-date"
                          sx={{ padding: "0px !important" }}
                        >
                          <ListItemButton
                            selected={dateRangeHeading === "Custom date range"}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              py: 1,
                              px: 2,
                              "&.Mui-selected": {
                                backgroundColor: "transparent",
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                            onClick={(event: any) => {
                              setIsCustomDate(true);
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              {dateRangeHeading === "Custom date range" && (
                                <CheckIcon sx={{ mr: 1, fontSize: 18 }} />
                              )}
                              <Typography
                                sx={{
                                  ml:
                                    dateRangeHeading === "Custom date range"
                                      ? 0
                                      : "26px",
                                }}
                              >
                                Custom
                              </Typography>
                            </Box>
                            {dateRangeHeading === "Custom date range" &&
                              startDate &&
                              endDate && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    ml: "26px",
                                    mt: 0.5,
                                  }}
                                >
                                  {format(new Date(startDate), "MMM d, yyyy")} -{" "}
                                  {format(new Date(endDate), "MMM d, yyyy")}
                                </Typography>
                              )}
                          </ListItemButton>
                        </MenuItem>,
                      ]
                    ) : (
                      <DateRange
                        editableDateInputs={true}
                        onChange={(item: any) =>
                          handleDateRange([item.selection], popupState)
                        }
                        moveRangeOnFirstSelection={false}
                        ranges={selectedDateRange}
                        dateDisplayFormat="dd-MM-yyyy"
                      />
                    )}
                  </StyledMenu>
                </SkeletonWrapper>
              </Grid>
              {userContext.loginDetail.userRole !== "user" && (
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={4}
                  lg={2}
                  className="search-bar pdf-icon"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "left",
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      backgroundColor: "primary.main",
                      color: "white",
                      padding: "8px",
                      borderRadius: "5px",
                    }}
                  >
                    Total:{" "}
                    {(resultData?.totalReceivedAmount ?? 0).toLocaleString(
                      "en-IN",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </Typography>
                  {resultData?.form3cForm25Coll?.length > 0 && (
                    <>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-search-pdf-icon"
                      >
                        <img
                          src="/xls.png"
                          alt=""
                          className="pdf-icon-hover"
                          onClick={() => {
                            openForm3CForm25ReportPopup("Excel");
                          }}
                        />
                      </SkeletonWrapper>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-search-pdf-icon"
                      >
                        <img
                          src="/pdf.png"
                          alt=""
                          className="pdf-icon-hover"
                          onClick={() => {
                            openForm3CForm25ReportPopup("pdf");
                          }}
                        />
                      </SkeletonWrapper>
                    </>
                  )}
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} mt={2}>
              <TableContainer
                sx={{
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Table
                  sx={{
                    minWidth: 900,
                  }}
                >
                  <EnhancedTableHead
                    numSelected={0}
                    onSelectAllClick={() => {}}
                    order={order}
                    orderBy={orderBy}
                    rowCount={0}
                  />
                  <TableBody>
                    {isLoading ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.form3cForm25Coll,
                          getComparator(order, orderBy),
                        ).map((item: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="center"
                              >
                                {item?.serviceDate
                                  ? format(
                                      new Date(item.serviceDate),
                                      "dd-MM-yyyy",
                                    )
                                  : ""}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                {item?.serialNumber}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                {item?.patientName}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="left"
                              >
                                {item?.consultedServices}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="right"
                              >
                                {item?.feesReceived != null
                                  ? `${parseFloat(
                                      item.feesReceived,
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : "0.00"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                                align="center"
                              >
                                {item?.receiptDate
                                  ? format(
                                      new Date(item.receiptDate),
                                      "dd-MM-yyyy",
                                    )
                                  : ""}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {resultData?.form3cForm25Coll?.length > 0 && (
                          <TableRow
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            <TableCell align="left" colSpan={4}>
                              <Typography sx={{ fontWeight: "bold" }}>
                                Page Total
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: "bold" }}>
                                {`${(
                                  resultData?.form3cForm25Coll?.reduce(
                                    (sum: number, item: any) =>
                                      sum +
                                      (parseFloat(item?.feesReceived) || 0),
                                    0,
                                  ) ?? 0
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                              </Typography>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        )}
                        {resultData?.form3cForm25Coll?.length <= 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.form3cForm25Coll?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-label"
                    >
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-stack"
                    >
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={curPage} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Form3CForm25Report;
